import { useCallback, useEffect, useState } from 'react';
import { Drawer, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import ButtonPrimaryLarge from '../../../components/common/ButtonPrimary/ButtonPrimaryLarge';
import ButtonSecondaryLarge from '../../../components/common/ButtonSecondary/ButtonSecondaryLarge';
import ButtonIconOutlineMedium from '../../../components/common/ButtonIconOutline/ButtonIconOutlineMedium';
import Tag from '../../../components/common/Tag/Tag';
import { openModalConfirm } from '../../../components/common/Modal/openModalConfirm';
import { Icon } from '../../../components/icon/icon';
import { PERMISSION, PERMISSION_FLAG } from '../../../constants/permissions';
import { can } from '../../../utils/permissions';
import {
  cancelCustomerConfirm,
  confirmCustomer,
  fetchCustomerHistory,
  fetchCustomerRooms,
  isConflictError,
} from '../../../services/customers/customersApi';
import { openMessageSuccess } from '../../../components/common/Message/openMessageSuccess';
import { openMessageError } from '../../../components/common/Message/openMessageError';

const formatTime = value =>
  value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-';

// Cùng kiểu thẻ trạng thái với trang đơn hàng: xanh lá = đã chốt, xanh dương = chưa chốt
const StatusTag = ({ confirmed }) => {
  const { t } = useTranslation();
  return confirmed ? (
    <Tag
      className="bg-[#E3F7E1] rounded-[4px] py-[4px] px-[8px] border-[1px] border-solid border-[#6DC563] text-[#6DC563]"
      label={
        <span className="text-label-medium font-semibold">
          {t('Confirmed')}
        </span>
      }
    />
  ) : (
    <Tag
      className="bg-[#F2F8FD] rounded-[4px] py-[4px] px-[8px] border-[1px] border-solid border-[#258FD2] text-[#258FD2]"
      label={
        <span className="text-label-medium font-semibold">
          {t('Not confirmed')}
        </span>
      }
    />
  );
};

const Section = ({ title, children }) => (
  <section className="flex p-16 flex-col items-start gap-12 self-stretch rounded-[16px] bg-white">
    <span className="text-title-medium-emphasized">{title}</span>
    {children}
  </section>
);

const InfoRow = ({ label, children }) => (
  <div className="flex items-start justify-between gap-16 self-stretch">
    <span className="text-label-medium text-black-medium shrink-0">
      {label}
    </span>
    <span className="text-body-medium text-right break-words">
      {children || '-'}
    </span>
  </div>
);

const HistoryList = ({ history }) => {
  const { t } = useTranslation();
  if (!history.length) {
    return <span className="text-body-medium">{t('No history')}</span>;
  }
  return (
    <div className="flex flex-col gap-12 self-stretch">
      {history.map(item => (
        <div
          key={item.id}
          className="flex flex-col gap-4 p-12 rounded-[8px] bg-backgrounds-virtual"
        >
          <div className="flex justify-between items-center">
            <StatusTag confirmed={item.action === 'confirm'} />
            <span className="text-body-small">
              {formatTime(item.created_at)}
            </span>
          </div>
          <span className="text-body-small break-all">
            {Object.entries(item.price_version ?? {})
              .map(([key, version]) => `${key}: v${version}`)
              .join(' · ')}
          </span>
        </div>
      ))}
    </div>
  );
};

const RoomList = ({ rooms }) => {
  const { t } = useTranslation();
  if (!rooms.length) {
    return <span className="text-body-medium">{t('No designs')}</span>;
  }
  return (
    <div className="grid grid-cols-2 gap-12 self-stretch">
      {rooms.map(room => (
        <div key={room.id} className="flex flex-col gap-4">
          {room.thumbnail && (
            <img
              src={room.thumbnail}
              alt={room.name}
              className="w-full aspect-video object-cover rounded-[8px]"
            />
          )}
          <span className="text-label-medium">{room.name}</span>
          <span className="text-body-small">{formatTime(room.updated_at)}</span>
        </div>
      ))}
    </div>
  );
};

/** Chi tiết khách: thông tin, chốt/huỷ chốt giá, các thiết kế và lịch sử chốt. */
const CustomerDetailDrawer = ({ customer, open, onClose, onChanged }) => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.auth);
  const canConfirm = can(user, PERMISSION.CUSTOMERS, PERMISSION_FLAG.CONFIRM);
  const [history, setHistory] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const customerId = customer?.id;
  const load = useCallback(async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const [historyList, roomList] = await Promise.all([
        fetchCustomerHistory(customerId),
        fetchCustomerRooms(customerId),
      ]);
      setHistory(historyList);
      setRooms(roomList);
    } catch {
      openMessageError({ content: t('Unable to load customer details') });
    } finally {
      setIsLoading(false);
    }
  }, [customerId, t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggleConfirm = async () => {
    const confirming = !customer.is_confirmed_contract;
    setIsBusy(true);
    try {
      const updated = await (confirming
        ? confirmCustomer(customer.id)
        : cancelCustomerConfirm(customer.id));
      openMessageSuccess({
        content: confirming
          ? t('Prices confirmed')
          : t('Confirmation cancelled'),
      });
      onChanged?.(updated);
      load();
    } catch (error) {
      // 409: trạng thái đã đổi ở nơi khác, tải lại để nút khớp với dữ liệu thật
      openMessageError({
        content: isConflictError(error)
          ? confirming
            ? t('Customer is already confirmed')
            : t('Customer is not confirmed')
          : t('Unable to update confirmation'),
      });
      if (isConflictError(error)) onChanged?.();
    } finally {
      setIsBusy(false);
    }
  };

  const confirmed = !!customer?.is_confirmed_contract;

  const askToggleConfirm = () =>
    openModalConfirm({
      title: confirmed
        ? t('Cancel price confirmation?')
        : t('Confirm current prices for this customer?'),
      description: confirmed
        ? t('The customer will read live prices again')
        : t('Current price versions will be locked for this customer'),
      icon: confirmed ? <Icon.ICWarning /> : undefined,
      okText: t('Confirm'),
      cancelText: t('Cancel'),
      onOk: toggleConfirm,
    });
  const ConfirmButton = confirmed ? ButtonSecondaryLarge : ButtonPrimaryLarge;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      closable={false}
      destroyOnHidden
      title={
        <>
          <span className="text-title-large-emphasized">{customer?.name}</span>
          <ButtonIconOutlineMedium icon={<Icon.ICClose />} onClick={onClose} />
        </>
      }
      classNames={{
        wrapper: 'shadow-none !w-[480px]',
        body: 'p-24 bg-backgrounds-virtual',
        header:
          '!p-24 [&_.ant-drawer-title]:gap-16 [&_.ant-drawer-title]:items-center [&_.ant-drawer-title]:justify-end [&_.ant-drawer-title]:flex [&_.ant-drawer-title]:flex-row-reverse',
        content: '!rounded-l-[16px] !rounded-r-[0px] !bg-fills-white',
        footer: '!p-0',
      }}
      footer={
        canConfirm && customer ? (
          <div className="flex p-24 items-center justify-end self-stretch">
            <ConfirmButton
              title={confirmed ? t('Cancel confirmation') : t('Confirm prices')}
              loading={isBusy}
              disabled={isBusy}
              onClick={askToggleConfirm}
            />
          </div>
        ) : null
      }
    >
      {customer && (
        <Spin spinning={isLoading}>
          <div className="flex flex-col gap-16">
            <Section title={t('Information')}>
              <InfoRow label={t('Status')}>
                <StatusTag confirmed={confirmed} />
              </InfoRow>
              <InfoRow label={t('Contract date')}>
                {customer.contract_date
                  ? formatTime(customer.contract_date)
                  : null}
              </InfoRow>
              <InfoRow label={t('Phone number')}>{customer.phone}</InfoRow>
              <InfoRow label={t('Email')}>{customer.email}</InfoRow>
              <InfoRow label={t('Address')}>{customer.address}</InfoRow>
              <InfoRow label={t('Note')}>{customer.note}</InfoRow>
            </Section>
            <Section title={`${t('Designs')} (${rooms.length})`}>
              <RoomList rooms={rooms} />
            </Section>
            <Section title={`${t('Confirmation history')} (${history.length})`}>
              <HistoryList history={history} />
            </Section>
          </div>
        </Spin>
      )}
    </Drawer>
  );
};

export default CustomerDetailDrawer;
